/**
 * AdminRecipesPage - Offizielle StrongFit-Rezepte für den Community-Katalog verwalten.
 * Admin kann Rezepte aus einem Foto erstellen und löschen. Backend: /api/admin/nutrition/recipes
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ChefHat, Camera, Loader2, Trash2, Flame, Beef, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToB64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const photoSrc = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}/api/portal/nutrition/uploads/${String(u).replace('/uploads/', '')}` : null);

const AdminRecipesPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/nutrition/recipes`, auth);
      setRecipes(res.data.recipes || []);
    } catch { toast.error('Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pick = async (e) => { const f = e.target.files?.[0]; if (f) { setImage(await fileToB64(f)); setDraft(null); } };

  const analyze = async () => {
    if (!image) { toast.error('Bitte Foto wählen'); return; }
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/admin/nutrition/recipes/from-photo`, { image_base64: image }, auth);
      setDraft(res.data.draft);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kein Rezept erkannt'); }
    finally { setAnalyzing(false); }
  };

  const save = async () => {
    if (!draft?.name) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/nutrition/recipes`, {
        name: draft.name, description: draft.description, category: draft.category,
        tags: draft.tags || [], servings: draft.servings || 1, steps: draft.steps || [],
        photo_url: draft.photo_url,
        ingredients: (draft.ingredients || []).map((i) => ({ name: i.name, amount_g: i.amount_g, unit: i.unit || 'g', calories: i.calories, protein: i.protein, carbs: i.carbs, fat: i.fat })),
      }, auth);
      toast.success('Rezept im Katalog veröffentlicht');
      setDialogOpen(false); setDraft(null); setImage(null);
      load();
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Rezept aus dem Katalog löschen?')) return;
    try { await axios.delete(`${API}/admin/nutrition/recipes/${id}`, auth); toast.success('Gelöscht'); load(); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ChefHat className="w-6 h-6 text-amber-500" /> Rezept-Katalog</h1>
          <p className="text-sm text-gray-500">Offizielle StrongFit-Rezepte für alle Kunden (Community)</p>
        </div>
        <Button onClick={() => { setDialogOpen(true); setDraft(null); setImage(null); }} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="admin-recipe-new">
          <Camera className="w-4 h-4 mr-2" /> Rezept aus Foto
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : recipes.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-gray-500">Noch keine Katalog-Rezepte. Erstelle eines aus einem Foto.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-recipe-grid">
          {recipes.map((r) => (
            <Card key={r.id} className="overflow-hidden" data-testid={`admin-recipe-${r.id}`}>
              {r.photo_url && <img src={photoSrc(r.photo_url)} alt="" className="w-full h-32 object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{r.name}</h3>
                    <p className="text-xs text-gray-500">{r.category || 'Rezept'} · {r.ingredients?.length || 0} Zutaten</p>
                  </div>
                  <button onClick={() => del(r.id)} className="text-red-400 hover:text-red-600" data-testid={`admin-recipe-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex gap-3 mt-2 text-xs text-gray-600">
                  <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5" />{Math.round(r.total_calories || 0)} kcal</span>
                  <span className="flex items-center gap-1"><Beef className="w-3.5 h-3.5" />{Math.round(r.total_protein || 0)} g</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setDraft(null); setImage(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-recipe-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Katalog-Rezept aus Foto</DialogTitle></DialogHeader>
          {!draft ? (
            <div className="space-y-3">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="admin-recipe-photo-input" />
              {image ? (
                <div className="relative"><img src={image} alt="" className="w-full max-h-56 object-contain rounded border" /><button className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1" onClick={() => setImage(null)}><X className="w-4 h-4" /></button></div>
              ) : (
                <Button variant="outline" className="w-full h-24 border-dashed" onClick={() => fileRef.current?.click()} data-testid="admin-recipe-photo-choose"><Camera className="w-5 h-5 mr-2" /> Foto wählen</Button>
              )}
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold recipe-cta" onClick={analyze} disabled={analyzing || !image} data-testid="admin-recipe-analyze">
                {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />KI analysiert…</> : 'Rezept erstellen'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {draft.photo_url && <img src={photoSrc(draft.photo_url)} alt="" className="w-full max-h-40 object-cover rounded border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
              <div><Label className="text-xs">Name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="admin-recipe-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Kategorie</Label><Input value={draft.category || ''} onChange={(e) => setDraft({ ...draft, category: e.target.value })} /></div>
                <div><Label className="text-xs">Tags (Komma)</Label><Input value={(draft.tags || []).join(', ')} onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} /></div>
              </div>
              <div><Label className="text-xs">Zutaten ({draft.ingredients?.length || 0})</Label>
                <div className="max-h-32 overflow-y-auto space-y-1 mt-1">
                  {(draft.ingredients || []).map((i, idx) => <div key={idx} className="text-xs bg-gray-50 rounded px-2 py-1 flex justify-between"><span>{i.name}</span><span className="text-gray-400">{i.amount_g}g · {Math.round(i.calories)}kcal</span></div>)}
                </div>
              </div>
              <div><Label className="text-xs">Zubereitung (1 Schritt/Zeile)</Label><Textarea rows={4} value={(draft.steps || []).join('\n')} onChange={(e) => setDraft({ ...draft, steps: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} /></div>
            </div>
          )}
          <DialogFooter>
            {draft && <Button variant="ghost" onClick={() => setDraft(null)}>Zurück</Button>}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            {draft && <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold recipe-cta" data-testid="admin-recipe-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Veröffentlichen</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRecipesPage;
